import assertNever from "assert-never";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { OnboardingVisitInstanceId, VisitDatesParams, VisitInstanceId } from "../messages";
import { STRINGS } from "../strings";
import { VisitType } from "../system/AppState/AppVisitsState";
import { globalize, translate } from "../translate";
import { HashSet } from "../utils/HashSet";
import { CheckedVisitData } from "./AvailableVisitActivity";
import { AppListItem } from "./Generic/AppList";
import Checkbox from "./Generic/Checkbox";
import { If } from "./Generic/If";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";

interface PropsTypes {
  Onboarding: {
    checkedVisitData: CheckedOnboardingVisitData;
    visitInstanceId: OnboardingVisitInstanceId;
  };
  Available: {
    checkedVisitData: CheckedVisitData;
    visitInstanceId: VisitInstanceId;
  };
}

interface Props<TVisitType extends VisitType> {
  allowMultipleCaregivers: boolean;
  checkedVisits: HashSet<VisitInstanceId | OnboardingVisitInstanceId>;
  requestedVisitBatch: boolean;
  areAllShiftsChecked: boolean;
  visitType: TVisitType;
  dates: {
    date: VisitDatesParams;
    visitInstanceId: PropsTypes[TVisitType]["visitInstanceId"];
  }[];
  onCheckVisit: (visitData: PropsTypes[TVisitType]["checkedVisitData"]) => void;
}

const AvailableSinglesDates = <TVisitType extends VisitType>(props: Props<TVisitType>) => {
  const formatDay = (value: LocalDateTime) =>
    globalize().formatLocalDateTime(value, { skeleton: "MMMMd" });

  const formatTime = (value: LocalDateTime) =>
    globalize().formatLocalDateTime(value, { time: "short" });

  const onCheckboxClick = (visitInstanceId: VisitInstanceId | OnboardingVisitInstanceId) => {
    switch (props.visitType) {
      case "Available":
        props.onCheckVisit({
          type: "Singles",
          visitInstanceId: visitInstanceId as VisitInstanceId,
        });
        break;
      case "Onboarding":
        props.onCheckVisit({
          type: "Singles",
          visitInstanceId: visitInstanceId as OnboardingVisitInstanceId,
        });
        break;
      default:
        assertNever(props.visitType);
    }
  };

  return (
    <>
      <If condition={props.allowMultipleCaregivers && props.dates.length > 1}>
        <AppListItem
          leading={
            <Checkbox
              checked={props.areAllShiftsChecked}
              disabled={props.requestedVisitBatch}
              onClick={() => props.onCheckVisit({ type: "AllShifts" })}
            />
          }
          title={translate(STRINGS.SELECT_ALL)}
        />
      </If>
      {props.dates.map(({ date, visitInstanceId }, i) => (
        <AppListItem
          key={date.startDateTime.toString()}
          leading={
            props.allowMultipleCaregivers && props.dates.length > 1 ? (
              <Checkbox
                checked={props.checkedVisits.has(visitInstanceId)}
                disabled={props.requestedVisitBatch}
                onClick={() => onCheckboxClick(visitInstanceId)}
              />
            ) : undefined
          }
          bulletNumber={!props.allowMultipleCaregivers || props.dates.length ? i + 1 : undefined}
          title={formatDay(date.startDateTime)}
          trailing={`${formatTime(date.startDateTime)} - ${formatTime(date.endDateTime)}`}
        />
      ))}
    </>
  );
};

export default AvailableSinglesDates;
